/**
 * premiumSale.js - DISPLAY copy of the Black Friday sale (banner, Pricing page, upgrade
 * spots, PMBOK 8 course price card): Premium half off for 3 months, and the PMBOK 8
 * Exam Prep course half off (one-time).
 *
 * The server decides the real price: functions/stripe/premiumSaleConfig.js. Keep the
 * dates and numbers here identical to that file (functions/stripe/premiumSale.test.js
 * fails if they drift). Banner text is client-side only, so SEO shells and page meta
 * never carry sale text.
 *
 * Admin preview: add ?previewSale=1 to any URL (admins only) to see the sale UI before
 * the window, ?previewSale=teaser to see the teaser, ?previewSale=0 to stop previewing.
 * The preview never changes what checkout charges.
 */

import { PMBOK8_PRICE } from "./pmbok8CourseConfig";

export const PREMIUM_SALE = {
  id: "bf2026",
  enabled: true,
  label: "Black Friday",
  timeZone: "America/New_York",
  start: "2026-11-27T00:00:00",
  end: "2026-11-30T23:59:59",
  percentOff: 50,
  durationInMonths: 3,
  regularMonthlyCents: 4900,
  // PMBOK 8 Exam Prep course (one-time): same window, this percent off PMBOK8_PRICE
  coursePercentOff: 50,
  // Optional "sale starts Nov 27" teaser before the window. Default OFF.
  teaserEnabled: false,
  teaserStart: "2026-11-20T00:00:00",
};

const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;

function tzOffsetMs(ms, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(ms));
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  return asUtc - Math.floor(ms / 1000) * 1000;
}

export function zonedWallTimeToUtcMs(wall, timeZone) {
  const m = WALL_RE.exec(String(wall));
  if (!m) return NaN;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  let result = guess - tzOffsetMs(guess, timeZone);
  const second = guess - tzOffsetMs(result, timeZone);
  if (second !== result) result = second;
  return result;
}

export const SALE_START_MS = zonedWallTimeToUtcMs(PREMIUM_SALE.start, PREMIUM_SALE.timeZone);
export const SALE_END_MS = zonedWallTimeToUtcMs(PREMIUM_SALE.end, PREMIUM_SALE.timeZone) + 1000; // exclusive
export const TEASER_START_MS = zonedWallTimeToUtcMs(PREMIUM_SALE.teaserStart, PREMIUM_SALE.timeZone);

const dollars = (cents) => {
  const v = cents / 100;
  return Number.isInteger(v) ? `$${v}` : `$${v.toFixed(2)}`;
};

export const SALE_PRICE_TEXT = dollars(Math.round(PREMIUM_SALE.regularMonthlyCents * (100 - PREMIUM_SALE.percentOff) / 100));
export const REGULAR_PRICE_TEXT = dollars(PREMIUM_SALE.regularMonthlyCents);
// "$24.50/month for your first 3 months, then $49/month"
export const SALE_TERMS_TEXT =
  `${SALE_PRICE_TEXT}/month for your first ${PREMIUM_SALE.durationInMonths} months, then ${REGULAR_PRICE_TEXT}/month`;
// One checkout button label everywhere during the sale
export const SALE_CTA_TEXT = `Start Premium at ${SALE_PRICE_TEXT}/month`;
// PMBOK 8 Exam Prep course (one-time)
export const COURSE_REGULAR_PRICE_TEXT = dollars(Math.round(PMBOK8_PRICE * 100));
export const COURSE_SALE_PRICE = Math.round(PMBOK8_PRICE * (100 - PREMIUM_SALE.coursePercentOff)) / 100;
export const COURSE_SALE_PRICE_TEXT = dollars(Math.round(COURSE_SALE_PRICE * 100));
export const SALE_ENDS_SHORT_TEXT = "ends Monday 11:59 PM ET";
export const SALE_ENDS_TEXT = "Ends Monday, Nov 30 at 11:59 PM ET";
export const SALE_STARTS_TEXT = "starts Friday, Nov 27 at 12:00 AM ET";

/** "active" | "teaser" | "none" from the real clock. */
export function salePhaseAt(nowMs, cfg = PREMIUM_SALE) {
  if (!cfg.enabled) return "none";
  if (nowMs >= SALE_START_MS && nowMs < SALE_END_MS) return "active";
  if (cfg.teaserEnabled && nowMs >= TEASER_START_MS && nowMs < SALE_START_MS) return "teaser";
  return "none";
}

/** Time left until the sale ends, for the countdown. */
export function timeLeft(nowMs) {
  const ms = Math.max(0, SALE_END_MS - nowMs);
  const s = Math.floor(ms / 1000);
  return { ms, days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
}
